// Pure rules for the File Manager's Move dialog (#393 bulk move, #394 folders).
//
// The dialog can now relocate a SET of documents and folders in one go, so
// "which destinations are valid?" is a question about the whole set, not one
// item. Kept out of the component so the rules are unit-tested without a DOM.
//
// A destination (null = root, otherwise a folder id) is blocked when:
//   - 'self'    — it IS one of the selected folders (a folder can't go into itself)
//   - 'inside'  — it is a DESCENDANT of a selected folder (that would orphan the
//                 whole subtree behind a cycle; the server refuses it too)
//   - 'current' — every selected item already lives there, so moving is a no-op
// Protected /Financials destinations are blocked by the picker itself.

/** Map of parent id (null = root) -> child folder ids, from the flat tree rows. */
export function buildChildrenMap(treeFolders = []) {
    const children = new Map();
    for (const f of treeFolders) {
        const parent = f.parent_id || null;
        if (!children.has(parent)) children.set(parent, []);
        children.get(parent).push(f.id);
    }
    return children;
}

/** Every folder id UNDER any of `rootIds` (the roots themselves are excluded). */
export function collectDescendantIds(rootIds = [], treeFolders = []) {
    const children = buildChildrenMap(treeFolders);
    const out = new Set();
    const stack = [...rootIds];
    while (stack.length) {
        const id = stack.pop();
        for (const child of children.get(id) || []) {
            if (!out.has(child)) {
                out.add(child);
                stack.push(child);
            }
        }
    }
    return out;
}

/**
 * Protected /Financials folders never move (they stay in their tree), so they
 * are split out of a selection rather than silently dropped.
 */
export function splitMovableFolders(folders = []) {
    const movable = [];
    const skippedProtected = [];
    for (const f of folders) (f.is_protected ? skippedProtected : movable).push(f);
    return { movable, skippedProtected };
}

/**
 * Build `reasonFor(destId) -> 'self' | 'inside' | 'current' | null` for a set.
 *
 * @param {Array<{id, folder_id?}>}            documents  items being moved
 * @param {Array<{id, parent_id?}>}            folders    movable folders being moved
 * @param {Array<{id, parent_id}>|null}        treeFolders the whole tree (null until loaded)
 */
export function makeDestinationRule({ documents = [], folders = [], treeFolders = null } = {}) {
    const folderIds = new Set(folders.map((f) => f.id));
    const descendants = treeFolders ? collectDescendantIds([...folderIds], treeFolders) : new Set();
    const total = documents.length + folders.length;

    return function reasonFor(destId) {
        const dest = destId || null;
        if (dest && folderIds.has(dest)) return 'self';
        if (dest && descendants.has(dest)) return 'inside';
        if (total > 0
            && documents.every((d) => (d.folder_id || null) === dest)
            && folders.every((f) => (f.parent_id || null) === dest)) {
            return 'current';
        }
        return null;
    };
}

/** "2 documents, 1 folder" — numeric parts only; the caller localises the words. */
export function describeSelection(documents = [], folders = []) {
    return { documents: documents.length, folders: folders.length, total: documents.length + folders.length };
}
